/**
 * Pick a file, or drop one on the box.
 *
 * Both, not one: the office works from a scanner folder and drags, and a phone or a
 * locked-down desktop needs the picker. The drop zone is a real `<label>` wrapping a
 * real `<input type="file">`, so keyboard and screen-reader users get the picker for
 * free rather than through a div pretending to be a button.
 *
 * Size and type are checked here as well as on the server. Not instead of — the server
 * is the one that decides — but finding out a 60 MB scan is too big *after* uploading
 * it over site broadband is the kind of thing that makes people stop using a tool.
 */

import { useId, useRef, useState } from 'react';

import { useI18n } from '@/i18n';
import {
  ALLOWED_UPLOAD_EXTENSIONS,
  ALLOWED_UPLOAD_MIME,
  isAcceptedUpload,
  isImageUpload,
  MAX_FILE_BYTES,
  MAX_IMAGE_BYTES,
} from '@/lib/api';

interface Props {
  onPick: (file: File) => void;
  /**
   * Several at once — a folder of course certificates is filed in one go, not twelve
   * trips through the same dialog. When set, `onPick` is not called; every accepted
   * file arrives here together, and the rejected ones are named under the box.
   */
  onPickMany?: (files: File[]) => void;
  /** Shown while an upload is in flight; the control is inert then. */
  busy?: boolean;
  disabled?: boolean;
  /** Overrides the default "drop a file here" line — e.g. naming the document wanted. */
  label?: string;
  className?: string;
}

function humanSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

export function FileDrop({
  onPick,
  onPickMany,
  busy = false,
  disabled = false,
  label,
  className = '',
}: Props) {
  const { t } = useI18n();
  const inputId = useId();
  const input = useRef<HTMLInputElement | null>(null);

  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inert = busy || disabled;

  /** Null when the file is fine, otherwise why not. */
  const problem = (file: File): string | null => {
    if (!isAcceptedUpload(file)) return t('documents.typeRejected');
    const cap = isImageUpload(file) ? MAX_IMAGE_BYTES : MAX_FILE_BYTES;
    if (file.size > cap) return t('documents.tooLarge', { size: humanSize(cap) });
    return null;
  };

  const accept = (list: FileList | null | undefined) => {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;

    if (onPickMany) {
      const good = files.filter((f) => problem(f) === null);
      const bad = files.filter((f) => problem(f) !== null);
      setError(
        bad.length > 0
          ? bad.map((f) => `${f.name}: ${problem(f)}`).join(' · ')
          : null,
      );
      if (good.length > 0) onPickMany(good);
      return;
    }

    const file = files[0]!;
    const why = problem(file);
    if (why) {
      setError(why);
      return;
    }
    setError(null);
    onPick(file);
  };

  return (
    <div className={className}>
      <label
        htmlFor={inputId}
        onDragOver={(e) => {
          if (inert) return;
          // Without preventDefault the browser navigates to the file instead.
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          if (inert) return;
          e.preventDefault();
          setOver(false);
          accept(e.dataTransfer.files);
        }}
        className={`flex flex-col items-center justify-center gap-1 px-4 py-6 rounded border-2 border-dashed text-center transition-colors ${
          inert
            ? 'border-border text-subtle cursor-not-allowed'
            : over
              ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)] cursor-pointer'
              : 'border-border hover:border-[var(--color-primary)] hover:bg-surface-alt cursor-pointer'
        }`}
      >
        <svg
          viewBox="0 0 20 20"
          aria-hidden="true"
          className="w-6 h-6 text-subtle"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 13.5V3.5M6.5 7 10 3.5 13.5 7" />
          <path d="M3.5 13v2.5a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V13" />
        </svg>

        <span className="text-sm font-medium">
          {busy ? t('documents.uploading') : (label ?? t('documents.dropHere'))}
        </span>
        <span className="text-xs text-subtle">{t('documents.accepted')}</span>

        <input
          id={inputId}
          ref={input}
          type="file"
          className="sr-only"
          disabled={inert}
          multiple={onPickMany !== undefined}
          // Extensions as well as types: Windows has no type for .p7m or HEIC, and a
          // type-only filter hid those files from the picker altogether.
          accept={[...ALLOWED_UPLOAD_MIME, ...ALLOWED_UPLOAD_EXTENSIONS].join(',')}
          onChange={(e) => {
            accept(e.target.files);
            // Reset, so picking the SAME file again after a failure still fires change.
            e.target.value = '';
          }}
        />
      </label>

      {error ? <p className="text-xs text-danger mt-1.5">{error}</p> : null}
    </div>
  );
}
